<script lang="ts">
  import { richTextFormatter } from '$lib/helpers/richTextHelper';
  import ThemedHeader from '$lib/components/website/ThemedHeader.svelte';
  import Breadcrumbs from '$lib/components/website/Breadcrumbs.svelte';
  import ProgramPageAuthorPanel from '$lib/components/website/ProgramPageAuthorPanel.svelte';
  import EquipmentPanel from '$lib/components/website/EquipmentPanel.svelte';
  import ReviewPanel from '$lib/components/website/review/ReviewPanel.svelte';
  import CtaPanel from '$lib/components/website/ctaPanel/CtaPanel.svelte';
  import AccordionPanel from '$lib/components/website/accordion/AccordionPanel.svelte';
  import CardPanel from '$lib/components/website/CardPanel.svelte';
  import { CARD_PANEL_HEADING_ALIGNMENT, CARD_PANEL_VARIANT } from '$lib/constants/constants';
  import ValuePropositionPanel from '$lib/components/website/ValuePropositionPanel.svelte';

  export let data;

  $: heroImage = data?.pageData?.heroImage;
  $: eyebrowText = data?.pageData?.eyebrowText;
  $: programName = data?.pageData?.programName;
  $: programSummary = data?.pageData?.programSummary;
  $: trainerDetails = data?.pageData?.trainerDetails;
  $: valuePropositions = data?.pageData?.valuePropositions;
  $: programOverview = data?.pageData?.programOverview;
  $: equipmentPanel = data?.pageData?.equipmentPanel;
  $: reviewPanel = data?.pageData?.reviewPanel;
  $: ctaPanel = data?.pageData?.ctaPanel;
  $: faQs = data?.pageData?.faQs;
  $: relatedProgramsCollection = data?.pageData?.relatedProgramsCollection;
  $: recommendedArticlesCollection = data?.pageData?.recommendedArticlesCollection;
</script>

<article class="flex flex-col items-center mb-16 lg:mb-24">
  <ThemedHeader
    backgroundColor="bg-primary-500"
    customClass="absolute h-[18.75rem] left-0 right-0 -z-10 lg:h-[20.125rem]"
  />
  {#if heroImage}
    <div class="w-full h-[23.25rem] max-w-1280 lg:px-8 md:h-[40.25rem] lg:mt-8">
      <picture>
        <source media="(min-width: 640px)" srcset="{heroImage?.url}?w=1800&q=85&fm=webp" />
        <source srcset="{heroImage?.url}?w=1000&q=85&fm=webp" />
        <img
          src="{heroImage?.url}?fm=webp"
          alt={heroImage?.title}
          class="w-full h-full object-cover lg:rounded-xl"
        />
      </picture>
    </div>
  {/if}
  <div
    class="flex flex-row justify-center items-center text-center text-sm md:text-md px-4 pt-8 md:pt-6"
  >
    <Breadcrumbs
      links={[
        { label: 'Sweat', href: '/' },
        { label: 'Programs', href: '/programs/' },
        {
          label: data?.pageData?.programName,
          href: `/programs${data?.pageData?.slug.toLowerCase()}`,
          isCurrentUrl: true,
        },
      ]}
      currentTextColor="text-primary-500"
      textColor="text-primary-200"
      chevronColor="#FFCCDD"
    />
  </div>
  <section class="w-full md:max-w-800 flex flex-col px-4 lg:px-0 mx-auto gap-8 lg:gap-16">
    <div class="text-center">
      <p class="text-primary-500 font-semibold text-sm md:text-md pt-8 md:pt-6 pb-4">
        {eyebrowText}
      </p>
      {#if programName}
        <h1
          class="text-center text-display-md md:text-display-xl font-bold text-gray-900 break-words"
        >
          {programName}
        </h1>
      {/if}
      {#if programSummary}
        <p class="text-gray-500 text-md md:text-xxl text-center break-words pt-4 md:pt-8 lg:pb-0">
          {programSummary}
        </p>
      {/if}
    </div>
    {#if !trainerDetails.trainerHref.includes(undefined)}
      <div class="pt-8">
        <ProgramPageAuthorPanel authorPanel={trainerDetails} />
      </div>
    {/if}
    {#if valuePropositions}
      <ValuePropositionPanel points={valuePropositions} />
    {/if}
    {#if programOverview}
      <div class="pt-8 lg:pt-0">
        <h2
          class="text-left text-display-xxs md:text-[1.875rem] font-semibold text-gray-900 pb-4 md:pb-5"
        >
          Program Overview
        </h2>
        <section class="text-left text-gray-600 pt-2 md:pt-4 space-y-6">
          {@html richTextFormatter(programOverview)}
        </section>
      </div>
    {/if}
  </section>
  <section class="w-full flex flex-col gap-24 lg:gap-48 mx-auto pt-24 lg:pt-[112px]">
    {#if equipmentPanel}
      <EquipmentPanel
        {equipmentPanel}
        eyelineTextOveride="Equipment"
        sectionClass="w-full lg:max-w-[1000px] xl:max-w-[1216px] px-4 lg:px-0 mx-auto"
      />
    {/if}
    {#if reviewPanel}
      <ReviewPanel
        eyebrowText={reviewPanel?.eyebrowText}
        title={reviewPanel?.title}
        backgroundVariant={reviewPanel?.backgroundVariant}
        reviewsCollectionItems={reviewPanel?.reviewsCollection?.items}
      />
    {/if}
    {#if ctaPanel}
      <div class="flex justify-center lg:-my-4">
        <div class="w-full">
          <CtaPanel {ctaPanel} />
        </div>
      </div>
    {/if}
    {#if faQs}
      <AccordionPanel accordionPanel={faQs} customClass="px-4 lg:px-0" />
    {/if}
    {#if relatedProgramsCollection.length > 0}
      <div class="max-w-[440px] sm:max-w-800 md:max-w-1280 lg:max-w-[100%] px-4 lg:px-0">
        <CardPanel
          heading="Related programs"
          subHeading="Programs"
          cardsCollection={relatedProgramsCollection}
          variant={data?.pageData?.relatedProgramsCollection?.length > 3
            ? CARD_PANEL_VARIANT.SLIDER
            : CARD_PANEL_VARIANT.GRID}
          headingAlignment={CARD_PANEL_HEADING_ALIGNMENT.CENTER}
          customClass="w-full lg:max-w-[1000px] xl:max-w-[1216px] mx-auto"
          customGridPanelClass="grid grid-cols-1 gap-8 items-start lg:grid-cols-3 px-4 lg:px-0 lg:gap-x-8 lg:gap-y-16 mt-8 md:mt-12"
        />
      </div>
    {/if}
    {#if recommendedArticlesCollection.length > 0}
      <div class="max-w-[440px] sm:max-w-800 md:max-w-1280 lg:max-w-[100%] px-4 lg:px-0">
        <CardPanel
          heading={data.pageData?.articlesHeading}
          subHeading={data.pageData?.articlesEyebrowText}
          cardsCollection={recommendedArticlesCollection}
          variant={CARD_PANEL_VARIANT.SLIDER}
          headingAlignment={CARD_PANEL_HEADING_ALIGNMENT.CENTER}
          customClass="w-full lg:max-w-[1000px] xl:max-w-[1216px] mx-auto"
          customGridPanelClass="grid grid-cols-1 gap-8 items-start md:grid-cols-3 px-4 lg:px-0 lg:gap-x-8 lg:gap-y-16 mt-8 md:mt-12"
        />
      </div>
    {/if}
  </section>
</article>
